import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Plus, Link2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useScrollLock, useFitViewport } from "../lib/menus";

export const FB_LABEL = { ok: "Facebook ok", error: "Facebook afbrudt", none: "Ikke forbundet" };
export const ACCESS_LABEL = { trial: "Prøveperiode", active: "Aktiv", past_due: "Mangler betaling", expired: "Udløbet", cancelled: "Stoppet", free: "Gratis", suspended: "Spærret" };

export function useGroupStores() {
  const { store } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!store || store.staff || store.is_owner) return;
    api.get("/account/stores", { netGuard: false }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [store?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return d;
}

function StoreItem({ s, onPick }) {
  return (
    <button type="button" role="menuitemradio" aria-checked={s.current} className={"ss-item" + (s.current ? " on" : "")}
      onClick={() => onPick(s)} data-testid={`store-switch-item-${s.id}`}>
      <span className="ss-check">{s.current && <Check size={16} />}</span>
      <span className="ss-txt">
        <b>{s.name}</b>
        <small>
          <i className={`ss-dot ${s.fb}`} /> {FB_LABEL[s.fb]} · {s.locked ? "Låst" : ACCESS_LABEL[s.access_kind] || s.access_kind}
        </small>
      </span>
    </button>
  );
}

export default function StoreSwitcher({ name, d }) {
  const { switchStore } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const off = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const show = () => { window.scrollTo({ top: 0 }); setOpen(true); };
    document.addEventListener("mousedown", off);
    window.addEventListener("jatak:open-store-switcher", show);
    return () => {
      document.removeEventListener("mousedown", off);
      window.removeEventListener("jatak:open-store-switcher", show);
    };
  }, []);
  const menuRef = useRef(null);
  useScrollLock(open);
  useFitViewport(menuRef, open);
  useEffect(() => {
    if (!open) return;
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);
  if (!d || d.stores.length < 2) return null;
  const pick = async (s) => {
    if (s.current) return setOpen(false);
    setBusy(true);
    await switchStore(s.id).catch(() => setBusy(false));
  };
  return (
    <div className="ss" ref={ref}>
      <button type="button" className="ss-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="store-switcher">
        <span className="ss-name">{busy ? "Skifter…" : name}</span>
        <ChevronDown size={16} />
      </button>
      {open && (
        <div className="ss-menu" role="menu" ref={menuRef} data-testid="store-switch-menu">
          <div className="ss-head">Dine butikker</div>
          {d.stores.map((s) => <StoreItem key={s.id} s={s} onPick={pick} />)}
          <div className="ss-sep" />
          <Link className="ss-link" to="/butikker#tilfoej" onClick={() => setOpen(false)} data-testid="store-switch-add"><Plus size={15} /> Tilføj butik</Link>
          <Link className="ss-link" to="/butikker#tilknyt" onClick={() => setOpen(false)} data-testid="store-switch-link"><Link2 size={15} /> Tilknyt eksisterende konto</Link>
        </div>
      )}
    </div>
  );
}
